import { useCallback, useEffect, useState } from 'react'
import { Check, Loader2, MessageCircle, Send } from 'lucide-react'
import {
  answerContextQuestion,
  getContextQuestions,
  type ContextQuestion,
} from '../lib/api'
import { timeAgo } from '../lib/utils'
import { businessHourFromUtc } from '../lib/businessTime'

/**
 * Home surface for one open Context-Keeper ask (DW-CK-ASKS-ON-HOME).
 *
 * The dedicated /client/questions page already works. Home never mounted the
 * ask, so continuous learning was a hidden sidebar. This card:
 * - Fetches open questions when principalId is set.
 * - Shows at most the FIRST open question (one ask, not a SOC queue).
 * - Heading: "Your team is asking" — never "alert", never a verdict.
 * - Empty list or fetch error → render NOTHING (quiet home stays quiet).
 * - Answer POSTs via answerContextQuestion then shows "Noted — thank you."
 */

// Same measured-facts phrasing as TeamQuestionsPage — kept local so that page
// is not rewritten. Show at most one supporting line on home.
function firstSupportingFactLine(facts: Record<string, unknown>): string | null {
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v : null

  const recentCount = num(facts.recent_count)
  const windowDays = num(facts.window_days)
  if (recentCount !== null && windowDays !== null) {
    const domain = str(facts.domain)
    const target = domain ? `to ${domain}` : 'to a new external domain'
    const plural = recentCount === 1 ? 'email' : 'emails'
    return (
      `${recentCount} ${plural} ${target} in the last ${windowDays} ` +
      `${windowDays === 1 ? 'day' : 'days'}`
    )
  }

  const deviceFamily = str(facts.device_family)
  if (deviceFamily) {
    const firstSeen = str(facts.first_seen)
    return firstSeen
      ? `First sign-in from a ${deviceFamily}, ${timeAgo(firstSeen)}`
      : `First sign-in from a ${deviceFamily}`
  }

  const recentHour = num(facts.recent_median_hour_utc)
  const priorHour = num(facts.prior_median_hour_utc)
  if (recentHour !== null && priorHour !== null) {
    // BRIEF-E5d: the business's own clock, never UTC.
    return (
      `Now most active around ${businessHourFromUtc(recentHour)}, ` +
      `was ${businessHourFromUtc(priorHour)}`
    )
  }

  for (const [key, value] of Object.entries(facts)) {
    if (num(value) === null && str(value) === null) continue
    const words = key.replace(/_/g, ' ').trim()
    const label = words.charAt(0).toUpperCase() + words.slice(1)
    return `${label}: ${value}`
  }
  return null
}

export function HomeContextAskCard({ principalId }: { principalId: string }) {
  const [question, setQuestion] = useState<ContextQuestion | null>(null)
  const [noted, setNoted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [answerText, setAnswerText] = useState('')
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setFailed(false)
    try {
      const data = await getContextQuestions(principalId)
      const open = data.filter((q) => q.status === 'open')
      setQuestion(open[0] ?? null)
    } catch {
      setFailed(true)
      setQuestion(null)
    }
  }, [principalId])

  useEffect(() => {
    void (async () => {
      setIsLoading(true)
      await load()
      setIsLoading(false)
    })()
  }, [load])

  const submitAnswer = useCallback(async () => {
    const text = answerText.trim()
    if (!text || busy || !question) return
    setBusy(true)
    setSubmitError(null)
    try {
      await answerContextQuestion(question.id, text)
      setQuestion(null)
      setNoted(true)
      setAnswerText('')
    } catch {
      setSubmitError('That didn’t send. Please try again.')
      setBusy(false)
    }
  }, [answerText, busy, question])

  // Quiet until we know; empty / error stay invisible on home.
  if (isLoading) return null
  if (failed) return null
  if (!question && !noted) return null

  if (noted && !question) {
    return (
      <section aria-label="Your team is asking" className="mb-5">
        <div className="rounded-2xl border border-gray-100 bg-white/60 p-3">
          <div className="flex items-start gap-2">
            <Check
              className="w-3.5 h-3.5 text-success mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-xs text-gray-600">Noted — thank you.</p>
          </div>
        </div>
      </section>
    )
  }

  if (!question) return null

  const factLine = firstSupportingFactLine(question.supporting_facts ?? {})

  return (
    <section aria-label="Your team is asking" className="mb-5">
      <h2 className="text-lg font-semibold text-navy flex items-center gap-2 mb-3">
        <MessageCircle className="w-5 h-5 text-brass" aria-hidden="true" />
        Your team is asking
      </h2>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-brass/10 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-4 h-4 text-brass" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            {question.subject && (
              <p className="text-xs font-medium text-brass mb-0.5">
                About {question.subject}
              </p>
            )}
            <p className="text-[15px] leading-snug text-navy">
              {question.question_text}
            </p>
            {factLine && (
              <p className="mt-2 text-xs text-gray-600 leading-relaxed rounded-xl bg-cream/70 border border-gray-100 p-3">
                {factLine}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3">
          <label htmlFor={`home-ck-answer-${question.id}`} className="sr-only">
            Your answer
          </label>
          <textarea
            id={`home-ck-answer-${question.id}`}
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            placeholder="Tell your team what’s going on…"
            rows={2}
            disabled={busy}
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40 disabled:opacity-60"
          />
          {submitError && (
            <p className="mt-1 text-xs text-danger" role="alert">
              {submitError}
            </p>
          )}
          <div className="mt-2 flex items-center justify-end">
            <button
              type="button"
              onClick={submitAnswer}
              disabled={busy || answerText.trim() === ''}
              className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-3.5 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-40 transition-colors"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="w-4 h-4" aria-hidden="true" />
              )}
              Send
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
